import type { Tile } from "../engine";
import { cardinalNeighbors } from "./roadBridgeNeighbors";
import { isSeaShoreLandAt } from "./seaShoreLand";

type CardinalNeighbors = ReturnType<typeof cardinalNeighbors>;
type DiagonalNeighbors = {
  northWest: Tile | undefined;
  northEast: Tile | undefined;
  southWest: Tile | undefined;
  southEast: Tile | undefined;
};

const OUTER_CORNERS = [
  {
    layerName: "outer-corner-north-west",
    diagonal: "northWest",
    cardinalA: "north",
    cardinalB: "west",
    dx: -1,
    dy: -1,
  },
  {
    layerName: "outer-corner-north-east",
    diagonal: "northEast",
    cardinalA: "north",
    cardinalB: "east",
    dx: 1,
    dy: -1,
  },
  {
    layerName: "outer-corner-south-west",
    diagonal: "southWest",
    cardinalA: "south",
    cardinalB: "west",
    dx: -1,
    dy: 1,
  },
  {
    layerName: "outer-corner-east-south",
    diagonal: "southEast",
    cardinalA: "south",
    cardinalB: "east",
    dx: 1,
    dy: 1,
  },
] as const satisfies readonly {
  layerName: string;
  diagonal: keyof DiagonalNeighbors;
  cardinalA: keyof CardinalNeighbors;
  cardinalB: keyof CardinalNeighbors;
  dx: number;
  dy: number;
}[];

const CARDINAL_OFFSETS = {
  north: { dx: 0, dy: -1 },
  east: { dx: 1, dy: 0 },
  south: { dx: 0, dy: 1 },
  west: { dx: -1, dy: 0 },
} as const satisfies Record<keyof CardinalNeighbors, { dx: number; dy: number }>;

function diagonalNeighbors({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): DiagonalNeighbors {
  const height = tiles.length / width;
  return {
    northWest: x > 0 && y > 0 ? tiles[(y - 1) * width + (x - 1)] : undefined,
    northEast: x < width - 1 && y > 0 ? tiles[(y - 1) * width + (x + 1)] : undefined,
    southWest: x > 0 && y < height - 1 ? tiles[(y + 1) * width + (x - 1)] : undefined,
    southEast: x < width - 1 && y < height - 1 ? tiles[(y + 1) * width + (x + 1)] : undefined,
  };
}

function needsOuterCorner({
  tiles,
  width,
  x,
  y,
  corner,
  cardinals,
  diagonals,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  corner: (typeof OUTER_CORNERS)[number];
  cardinals: CardinalNeighbors;
  diagonals: DiagonalNeighbors;
}): boolean {
  const diagonal = diagonals[corner.diagonal];
  const offsetA = CARDINAL_OFFSETS[corner.cardinalA];
  const offsetB = CARDINAL_OFFSETS[corner.cardinalB];
  if (!isSeaShoreLandAt({ tiles, width, x: x + corner.dx, y: y + corner.dy, tile: diagonal })) {
    return false;
  }

  return (
    !isSeaShoreLandAt({
      tiles,
      width,
      x: x + offsetA.dx,
      y: y + offsetA.dy,
      tile: cardinals[corner.cardinalA],
    }) &&
    !isSeaShoreLandAt({
      tiles,
      width,
      x: x + offsetB.dx,
      y: y + offsetB.dy,
      tile: cardinals[corner.cardinalB],
    })
  );
}

export function resolveSeaShoreOuterCornerLayerNames({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): string[] {
  const cardinals = cardinalNeighbors({ tiles, width, x, y });
  const diagonals = diagonalNeighbors({ tiles, width, x, y });

  return OUTER_CORNERS.flatMap((corner) =>
    needsOuterCorner({ tiles, width, x, y, corner, cardinals, diagonals })
      ? [corner.layerName]
      : [],
  );
}
